import { App } from '@capacitor/app';
import BaseService from './BaseService';
import AppService from './AppService';

/**
 * Handler registrado por los servicios que reaccionan al ciclo de vida.
 */
export type LifecycleHandler = () => void;

/**
 * Centralizes app lifecycle listeners shared across services.
 */
class AppLifecycleService extends BaseService {

    static initialized = false;
    static appActiveHandlers = new Set<LifecycleHandler>();
    static appInactiveHandlers = new Set<LifecycleHandler>();

    /** Si la app nativa está en segundo plano, según el último appStateChange. */
    static inBackground = false;

    /**
     * Si la app está en segundo plano y nadie la ve. En nativo lo dice
     * appStateChange; en la PWA, la visibilidad de la página.
     */
    static isInBackground(): boolean {
        return this.inBackground || document.visibilityState === 'hidden';
    }

    /**
     * Registers a handler fired when the app becomes active again.
     */
    static addAppActiveHandler(handler: LifecycleHandler): void {
        if (typeof handler === 'function') {
            this.appActiveHandlers.add(handler);
        }
    }

    /**
     * Unregisters a previously registered active handler.
     */
    static removeAppActiveHandler(handler: LifecycleHandler): void {
        this.appActiveHandlers.delete(handler);
    }

    /**
     * Registers a handler fired when the app goes to background.
     */
    static addAppInactiveHandler(handler: LifecycleHandler): void {
        if (typeof handler === 'function') {
            this.appInactiveHandlers.add(handler);
        }
    }

    /**
     * Unregisters a previously registered inactive handler.
     */
    static removeAppInactiveHandler(handler: LifecycleHandler): void {
        this.appInactiveHandlers.delete(handler);
    }

    /**
     * Initializes the shared listener for app active state.
     */
    static init(): void {
        if (this.initialized || !AppService.isNativeApp()) {
            return;
        }

        this.initialized = true;
        App.addListener('appStateChange', (state) => {
            this.inBackground = !state?.isActive;
            if (state?.isActive) {
                this.notifyAppActiveHandlers();
                return;
            }

            this.notifyAppInactiveHandlers();
        });

        // Android back button. Capacitor only navigates the webview history
        // when no listener is registered, so without this the press is
        // swallowed on the first history entry and the app cannot be closed.
        App.addListener('backButton', ({ canGoBack }) => {
            if (AppService.appcomponent?.closeTopOverlay()) {
                return;
            }

            if (canGoBack) {
                window.history.back();
                return;
            }

            App.exitApp();
        });
    }

    /**
     * Notifies all registered app active handlers.
     */
    static notifyAppActiveHandlers(): void {
        this.appActiveHandlers.forEach((handler) => {
            try {
                handler();
            } catch (error) {
                console.warn('AppLifecycleService active handler error', error);
            }
        });
    }

    /**
     * Notifies all registered app inactive handlers.
     */
    static notifyAppInactiveHandlers(): void {
        this.appInactiveHandlers.forEach((handler) => {
            try {
                handler();
            } catch (error) {
                console.warn('AppLifecycleService inactive handler error', error);
            }
        });
    }
}

export default AppLifecycleService;
